<script lang="ts" setup>
import { PropType } from 'vue'
import { useI18n } from '#i18n'
const { formatToFullDate } = useFullDateFormater()
const { t } = useI18n()

const props = defineProps({
  composer: {
    type: Object as PropType<Composer>,
    required: true,
  },
  activeOutlineId: {
    type: String,
    required: true,
  },
  resume: {
    type: String,
    required: false,
    default: null,
  },
  showCopyright: {
    type: Boolean,
    default: true,
  },
  hideOutline: {
    type: Boolean,
    default: false,
  },
  stickySidebar: {
    type: Boolean,
    default: false,
  },
})

const updatedAt = computed<string>(() => {
  return formatToFullDate(props.composer?.manualUpdateDate)
})
const copyrights = computed<Copyright[]>(() => {
  if (props.composer?.biography) {
    return props.composer?.biography?.copyrights.map((copyright) => ({
      name: `${copyright?.name}, ${new Date(
        props.composer?.biography?.publishingStartDate
      ).getFullYear()}`,
    }))
  } else if (props.composer?.workcourse) {
    return props.composer?.workcourse?.copyrights.map((copyright) => ({
      name: `${copyright?.name}, ${new Date(
        props.composer?.workcourse?.publishingStartDate
      ).getFullYear()}`,
    }))
  }
  return []
})

const slots = useSlots()
const slotToPass = Object.keys(slots).filter(
  (x) => !['prose', 'default'].includes(x)
)

const name = computed<string>(() => {
  if (!props.composer) return ''
  if (props.composer?.slug?.includes('baudin-bluebell')) {
    return `${props.composer?.firstName ?? ''} ${
      props.composer.lastName ?? ''
    } (Salah's Godson)`
  }
  return `${props.composer?.firstName ?? ''} ${props.composer.lastName ?? ''}`
})

// Menu/Outline
const outline = computed<SidebarMenu>(() => {
  if (props.hideOutline) {
    return { items: [] }
  }
  const {
    hasBooks,
    hasPartitions,
    hasMonographs,
    hasFloraAudios,
    hasPeriodicals,
    hasWorks,
    hasAnalyses,
    hasArticles,
    hasAudios,
    hasVideos,
    hasBiography,
    hasWorkcourse,
    resources,
  } = props.composer ?? {}
  const res: SidebarMenuItems = []
  if (hasBiography) {
    res.push({
      id: 'biography',
      resourceIcon: 'Composer',
      href: `/composer/${props.composer?.slug}/biography`,
      title: t('labels.biography'),
    })
  }
  if (hasWorkcourse) {
    res.push({
      id: 'workcourse',
      resourceIcon: 'Workcourse',
      href: `/composer/${props.composer?.slug}/workcourse`,
      title: t('labels.workcourse'),
    })
  }
  if (hasWorks) {
    res.push(
      {
        id: 'list_of_work_by_date',
        resourceIcon: 'List',
        href: `/composer/${props.composer?.slug}/worksByDate`,
        title: t('labels.list_of_work', 1),
      },
      {
        id: 'list_of_work_by_kind',
        resourceIcon: 'List',
        href: `/composer/${props.composer?.slug}/worksByKind`,
        title: t('labels.list_of_work', 2),
      }
    )
  }
  if (resources) {
    res.push({
      id: 'resources',
      resourceIcon: 'Resources',
      href: `/composer/${props.composer?.slug}/resources`,
      title: t('labels.resources'),
    })
  }
  if (hasBooks) {
    res.push({
      id: 'books',
      resourceIcon: 'Book',
      href: `/composer/${props.composer?.slug}/flora?type=books`,
      title: t('labels.book', 2),
    })
  }
  if (hasPartitions) {
    res.push({
      id: 'partitions',
      resourceIcon: 'Score',
      href: `/composer/${props.composer?.slug}/flora?type=partitions`,
      title: t('labels.score', 2),
    })
  }
  if (hasMonographs) {
    res.push({
      id: 'monographs',
      resourceIcon: 'Monographs',
      href: `/composer/${props.composer?.slug}/flora?type=monographs`,
      title: t('labels.monographs'),
    })
  }
  if (hasPeriodicals) {
    res.push({
      id: 'periodicals',
      resourceIcon: 'Periodicals',
      href: `/composer/${props.composer?.slug}/flora?type=periodicals`,
      title: t('labels.periodicals'),
    })
  }
  if (hasFloraAudios) {
    res.push({
      id: 'floraAudios',
      resourceIcon: 'Audio',
      href: `/composer/${props.composer?.slug}/flora?type=floraAudios`,
      title: t('labels.disc'),
    })
  }
  if (hasAnalyses) {
    res.push({
      id: 'analysis',
      resourceIcon: 'Analysis',
      href: `/composer/${props.composer?.slug}/analysis`,
      title: t('labels.analysis', 2),
    })
  }
  if (hasArticles) {
    res.push({
      id: 'articles',
      resourceIcon: 'Articles',
      href: `/composer/${props.composer?.slug}/articles`,
      title: t('labels.articles'),
    })
  }
  if (hasVideos) {
    res.push({
      id: 'video',
      resourceIcon: 'Video',
      href: `/composer/${props.composer?.slug}/videos`,
      title: t('labels.video', 2),
    })
  }
  if (hasAudios) {
    res.push({
      id: 'audio',
      resourceIcon: 'Audio',
      href: `/composer/${props.composer?.slug}/audios`,
      title: t('labels.audio', 2),
    })
  }
  res.forEach((item) => {
    if (item.id === props.activeOutlineId) {
      item.isActive = true
    }
  })
  return { items: res }
})
</script>
<template>
  <DetailView
    :title="name"
    :updated-at="updatedAt"
    :outline="outline"
    icon-for-type="Composer"
    :copyrights="copyrights"
    :sticky-sidebar="stickySidebar"
  >
    <template #heroDescription>{{ resume }}</template>
    <DataProse v-if="$slots.prose" class="mt-6 md:mt-0">
      <template #default="{ classes }">
        <slot name="prose" :classes="classes"></slot>
      </template>
    </DataProse>
    <slot></slot>
    <!-- Pass remaining slots -->
    <template v-for="name in slotToPass" #[name]="slotData">
      <slot :name="name" v-bind="slotData" />
    </template>
  </DetailView>
</template>
